/**
 * What the browser tab says.
 *
 * Every route in the app rendered under the one title compiled into
 * index.html — "Reppr — run the whole book from one desk". So a superadmin
 * on /admin, an agent on /deals and an athlete in the portal all had
 * identical tabs, identical bookmarks and identical history entries, and a
 * dozen open clients were twelve copies of the same marketing line. The tab
 * is a label like any other, and it was naming the wrong thing.
 *
 * Two rules here:
 *
 * 1. IN-APP PAGES PUT THE DISTINGUISHING PART FIRST. A tab is about twenty
 *    characters wide before it truncates, so "Micah Parsons — Reppr" is
 *    useful and "Reppr — Micah Parsons" is twelve identical tabs again.
 *    Marketing pages keep the brand first, because that is what a search
 *    result and a shared link should read.
 *
 * 2. THE TAB MATCHES THE NAV. Where the sidebar says "The Book", "Money" or
 *    "The Desk", so does the title — not the route name, and not a second
 *    invented vocabulary for the same screen.
 *
 * Detail pages name their record through `useDocumentTitle` once it loads.
 * Until then they carry the generic route title below, which is the honest
 * answer: we know which kind of page this is and not yet which record.
 */

import { matchRoutes } from 'react-router-dom'

export const BRAND = 'Reppr'

/** An in-app page: what it is, then whose app it is. */
const page = (name: string) => `${name} — ${BRAND}`

/**
 * Every route the app can be on, in the same shape `matchRoutes` wants.
 *
 * Order does not matter — react-router scores static segments above dynamic
 * ones, so /deals/new beats /deals/:dealId without help. This list is meant
 * to stay level with App.tsx; the test walks both and fails when it drifts.
 */
export const ROUTE_TITLES: { path: string; title: string }[] = [
  // ── Public ────────────────────────────────────────────────────────────
  { path: '/', title: `${BRAND} — run the whole book from one desk` },
  { path: '/athletes', title: `${BRAND} — for athletes and families` },
  { path: '/pricing', title: `Pricing — ${BRAND}` },
  { path: '/terms', title: `Terms of service — ${BRAND}` },
  { path: '/privacy', title: `Privacy policy — ${BRAND}` },

  // ── Getting in ────────────────────────────────────────────────────────
  { path: '/login', title: page('Sign in') },
  { path: '/register', title: page('Create your agency') },
  { path: '/forgot-password', title: page('Reset your password') },
  { path: '/reset-password', title: page('Choose a new password') },
  { path: '/verify-email', title: page('Verify your email') },
  { path: '/payment-success', title: page('Payment received') },
  { path: '/onboarding', title: page('Set up your agency') },
  { path: '/subscription-required', title: page('Subscription required') },

  // ── Shared links ──────────────────────────────────────────────────────
  { path: '/report/:shareToken', title: page('Shared report') },
  { path: '/scout/package/:shareToken', title: page('Player package') },
  { path: '/partners/:token', title: page('Partner page') },
  { path: '/connect/:token', title: page('Connect your account') },

  // ── Desk ──────────────────────────────────────────────────────────────
  { path: '/dashboard', title: page('Needs you') },
  { path: '/signals', title: page('Signals') },

  // ── Analytics & money ─────────────────────────────────────────────────
  { path: '/analytics', title: page('Analytics') },
  { path: '/incentives', title: page('Incentives') },
  { path: '/commissions', title: page('Commissions') },
  { path: '/revenue', title: page('Money') },
  { path: '/needs-you', title: page('Needs you') },
  { path: '/outreach', title: page('Outreach') },
  { path: '/portal-access', title: page('Portal access') },
  { path: '/audience', title: page('Audience') },

  // ── Roster ────────────────────────────────────────────────────────────
  { path: '/clients', title: page('The Book') },
  { path: '/clients/new', title: page('New client') },
  { path: '/clients/:clientId', title: page('Client') },
  { path: '/clients/:clientId/edit', title: page('Edit client') },
  { path: '/clients/:clientId/earnings/print', title: page('Earnings statement') },

  { path: '/prospects', title: page('Prospects') },
  { path: '/prospects/new', title: page('New prospect') },
  { path: '/prospects/:prospectId', title: page('Prospect') },
  { path: '/prospects/:prospectId/edit', title: page('Edit prospect') },

  { path: '/contacts', title: page('Contacts') },
  { path: '/contacts/new', title: page('New contact') },
  { path: '/contacts/:contactId', title: page('Contact') },
  { path: '/contacts/:contactId/edit', title: page('Edit contact') },

  { path: '/organizations', title: page('Organizations') },
  { path: '/organizations/new', title: page('New organization') },
  { path: '/organizations/:organizationId', title: page('Organization') },
  { path: '/organizations/:organizationId/edit', title: page('Edit organization') },

  // ── Deals ─────────────────────────────────────────────────────────────
  { path: '/deals', title: page('Pipeline') },
  { path: '/deals/templates', title: page('Deal templates') },
  { path: '/deals/compare', title: page('Compare deals') },
  { path: '/deals/win-loss', title: page('Win/loss report') },
  { path: '/deals/new', title: page('New deal') },
  { path: '/deals/:dealId', title: page('Deal') },
  { path: '/deals/:dealId/edit', title: page('Edit deal') },

  { path: '/player-transactions', title: page('Player transactions') },
  { path: '/player-transactions/new', title: page('New player transaction') },
  { path: '/player-transactions/:transactionId/edit', title: page('Edit player transaction') },

  // ── Compliance ────────────────────────────────────────────────────────
  { path: '/compliance', title: page('Compliance') },
  { path: '/compliance/templates', title: page('Compliance templates') },
  { path: '/compliance/calendar', title: page('Compliance calendar') },
  { path: '/compliance/new', title: page('New compliance item') },
  { path: '/compliance/:complianceId', title: page('Compliance item') },
  { path: '/compliance/:complianceId/edit', title: page('Edit compliance item') },

  // ── Work ──────────────────────────────────────────────────────────────
  { path: '/calendar', title: page('Calendar') },
  { path: '/calendar/new', title: page('New event') },
  { path: '/calendar/:eventId', title: page('Event') },
  { path: '/calendar/:eventId/edit', title: page('Edit event') },

  { path: '/tasks', title: page('Tasks') },
  { path: '/tasks/new', title: page('New task') },
  { path: '/tasks/:taskId', title: page('Task') },
  { path: '/tasks/:taskId/edit', title: page('Edit task') },

  { path: '/vendors', title: page('Vendors') },
  { path: '/activities', title: page('Activity') },
  { path: '/search', title: page('Search') },
  { path: '/client-inbox', title: page('Inbox') },
  { path: '/notifications', title: page('Notifications') },
  { path: '/tags', title: page('Tags') },
  { path: '/goals', title: page('Goals') },
  { path: '/relationships', title: page('Relationships') },
  { path: '/help', title: page('Help & support') },

  // ── Financial ─────────────────────────────────────────────────────────
  { path: '/contracts', title: page('Contracts') },
  { path: '/contracts/new', title: page('New contract') },
  { path: '/contracts/:contractId', title: page('Contract') },
  { path: '/contracts/:contractId/edit', title: page('Edit contract') },

  { path: '/payments', title: page('Payments') },

  { path: '/invoices', title: page('Invoices') },
  { path: '/invoices/new', title: page('New invoice') },
  { path: '/invoices/:invoiceId', title: page('Invoice') },
  { path: '/invoices/:invoiceId/edit', title: page('Edit invoice') },

  { path: '/transactions', title: page('Transactions') },
  { path: '/transactions/new', title: page('New transaction') },
  { path: '/transactions/:transactionId', title: page('Transaction') },
  { path: '/transactions/:transactionId/edit', title: page('Edit transaction') },

  // ── Reports ───────────────────────────────────────────────────────────
  { path: '/reports', title: page('Reports') },
  { path: '/reports/builder', title: page('Report builder') },
  { path: '/reports/portfolio', title: page('Portfolio report') },
  { path: '/reports/player-package', title: page('Player package') },
  { path: '/reports/player-package/:clientId', title: page('Player package') },

  // ── Agency admin ──────────────────────────────────────────────────────
  { path: '/settings', title: page('Settings') },
  { path: '/team', title: page('Team') },
  { path: '/admin', title: page('Admin panel') },
  { path: '/admin/agency-setup', title: page('Agency setup') },
  { path: '/admin/data-health', title: page('Data health') },
  { path: '/pending-customers', title: page('Pending customers') },

  // ── Client portal ─────────────────────────────────────────────────────
  { path: '/portal/login', title: page('Athlete sign in') },
  { path: '/portal/start', title: page('Athlete sign in') },
  { path: '/portal/verify', title: page('Verify your sign in') },
  { path: '/portal/verify/:token', title: page('Verify your sign in') },
  { path: '/portal', title: page('Your money') },
  { path: '/portal/finances', title: page('Your money') },
  { path: '/portal/schedule', title: page('Your schedule') },
  { path: '/portal/deals', title: page('Your deals') },
  { path: '/portal/deals/:id', title: page('Deal') },
  { path: '/portal/contracts', title: page('Your contracts') },
  { path: '/portal/contracts/:id', title: page('Contract') },
  { path: '/portal/incentives', title: page('Your incentives') },
  { path: '/portal/messages', title: page('Messages') },
  { path: '/portal/documents', title: page('Your documents') },
  { path: '/portal/actions', title: page('Action items') },
  { path: '/portal/compliance', title: page('Compliance') },
  { path: '/portal/vendors', title: page('Vendors') },
  { path: '/portal/profile', title: page('Your profile') },

  // ── Redirects, which are on screen only for an instant ────────────────
  { path: '/auth', title: page('Sign in') },
  { path: '/deals/pipeline', title: page('Pipeline') },
  { path: '/advanced-analytics', title: page('Analytics') },
  { path: '/portfolio-analytics', title: page('Analytics') },
  { path: '/__shots/:shot', title: page('Screenshot harness') },

  // Anything else is the app's own 404, so this list cannot miss.
  { path: '*', title: page('Page not found') },
]

/**
 * The title for a path.
 *
 * `matchRoutes` can return null in general, but not against this list: the
 * `*` entry matches everything. That branch is unreachable, and it returns
 * null rather than guessing a title, so a miss would leave the tab alone
 * instead of labelling the page something it is not.
 */
export function titleForPath(pathname: string): string | null {
  const matched = matchRoutes(ROUTE_TITLES, pathname)
  if (!matched?.length) return null
  return (matched[matched.length - 1].route as { title: string }).title
}

/** A record's own name in the tab, e.g. "Micah Parsons — Reppr". */
export function titleForRecord(name: string): string {
  return page(name)
}
